import { Swiper as SwiperContainer, SwiperSlide } from 'swiper/react'
import clsx from 'clsx'
import SwiperCore, { SwiperOptions } from 'swiper'
import { BasicStyledComponent } from 'shared-definitions/types'
import React, { useCallback, useRef, useState } from 'react'
import styles from './HorizontalGalleryAlternate.module.css'

import 'swiper/css'

interface RenderPaginationOpts {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

interface HorizontalGalleryAlternateProps<T>
  extends Pick<SwiperOptions, 'spaceBetween' | 'breakpoints'>,
    BasicStyledComponent {
  initial: readonly T[]
  slidesPerView: number
  sameHeight?: boolean
  slidesOffsetBefore?: number
  slidesOffsetAfter?: number
  render: (item: T, index: number) => React.ReactElement
  renderPagination?: (opts: RenderPaginationOpts) => React.ReactNode
}

const HorizontalGalleryAlternate = <T,>({
  slidesOffsetAfter,
  slidesOffsetBefore,
  sameHeight,
  className,
  render,
  initial,
  slidesPerView,
  spaceBetween,
  breakpoints,
  renderPagination,
}: HorizontalGalleryAlternateProps<T>): React.ReactElement => {
  const instance = useRef<SwiperCore | null>(null)

  const [possibleSlidePositions, setPossibleSlidePositions] = useState(
    initial.length - slidesPerView + 1
  )
  const showProgressBar = possibleSlidePositions > 1

  const [currentIndex, setCurrentIndex] = useState(0)

  return (
    <div
      className={clsx(className, styles.container, {
        [styles.hiddenProgress]: !showProgressBar,
      })}
    >
      <SwiperContainer
        slidesOffsetBefore={slidesOffsetBefore}
        slidesOffsetAfter={slidesOffsetAfter}
        centerInsufficientSlides
        onSwiper={useCallback((i: SwiperCore) => (instance.current = i), [])}
        onSlideChange={useCallback(
          (core: SwiperCore) => {
            const index = core.realIndex ?? 0
            setCurrentIndex(index >= possibleSlidePositions ? possibleSlidePositions - 1 : index)
          },
          [possibleSlidePositions]
        )}
        onBreakpoint={useCallback(
          (_core: SwiperCore, params: SwiperOptions) => {
            if (typeof params.slidesPerView === 'number') {
              setPossibleSlidePositions(initial.length - params.slidesPerView + 1)
            }
          },
          [initial.length]
        )}
        spaceBetween={spaceBetween}
        breakpoints={breakpoints}
        slidesPerView={slidesPerView}
        freeMode={true}
      >
        {initial.map((item, index) => (
          <SwiperSlide className={clsx({ [styles.slideSameHeight]: sameHeight })} key={index}>
            {render(item, index)}
          </SwiperSlide>
        ))}
      </SwiperContainer>

      {renderPagination?.({
        currentPage: currentIndex,
        totalPages: possibleSlidePositions,
        onPageChange: p => {
          instance.current?.slideTo(p)
        },
      })}
    </div>
  )
}

export default HorizontalGalleryAlternate
